'use client';

import { useLocale } from 'next-intl';
import { useRef, useState, useTransition } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Globe, Loader2 } from 'lucide-react';
import { setUserLocale } from '@/i18n/actions';
import { type Locale } from '@/i18n/locales';

const languages = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'ar', name: 'العربية', flag: '🇸🇦' },
  { code: 'tr', name: 'Türkçe', flag: '🇹🇷' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺' },
];

export default function LanguageSwitcher() {
  const locale = useLocale();
  const router = useRouter();
  const pathname = usePathname();
  const [isPending, startTransition] = useTransition();
  // The dropdown content is portaled to <body>, i.e. OUTSIDE the storefront's
  // `data-sf-theme="dark"` scope — so it would render on a white popover in dark
  // mode. Detect dark from the trigger's ancestor when opening and theme the
  // portaled content explicitly (same approach as the mobile-menu Sheet).
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isDark, setIsDark] = useState(false);

  const handleLanguageChange = (newLocale: string) => {
    startTransition(async () => {
      // Set the locale cookie
      await setUserLocale(newLocale as Locale);

      // Build the new URL from the visible browser path. Storefront slug
      // routes are internally rewritten to `/`, so `usePathname()` can drop
      // the tenant segment and send customers to the marketing landing.
      let newPath =
        typeof window !== 'undefined' && window.location.pathname
          ? window.location.pathname
          : pathname || '/';

      // Remove current locale prefix if exists
      const localePattern = /^\/(de|en|fr|es|ar|tr|ru)(\/|$)/;
      newPath = newPath.replace(localePattern, '/');

      // Special handling for blog posts - redirect to blog homepage
      // because slugs are different per language
      if (newPath.startsWith('/blog/') && newPath !== '/blog') {
        newPath = '/blog';
      }

      // Add new locale prefix (except for default locale 'de')
      if (newLocale !== 'de') {
        newPath = `/${newLocale}${newPath}`;
      }

      // Redirect to the new URL
      router.push(newPath);
      router.refresh();
    });
  };

  const currentLanguage = languages.find((lang) => lang.code === locale) || languages[0];

  return (
    <DropdownMenu
      onOpenChange={(open) => {
        if (open) {
          setIsDark(!!triggerRef.current?.closest('[data-sf-theme="dark"]'));
        }
      }}
    >
      <DropdownMenuTrigger asChild>
        <Button
          ref={triggerRef}
          variant="outline"
          size="sm"
          className="gap-2"
          disabled={isPending}
          aria-label={`Sprache wechseln (aktuell: ${currentLanguage.name})`}
        >
          {isPending ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Globe className="h-4 w-4" aria-hidden="true" />
          )}
          <span className="hidden sm:inline" aria-hidden="true">
            {currentLanguage.flag}
          </span>
          <span className="hidden md:inline" aria-hidden="true">
            {currentLanguage.name}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        data-sf-theme={isDark ? 'dark' : undefined}
        style={
          isDark
            ? {
                backgroundColor: '#171717',
                color: '#f5f5f5',
                borderColor: 'rgba(255,255,255,0.12)',
              }
            : undefined
        }
      >
        {languages.map((language) => (
          <DropdownMenuItem
            key={language.code}
            onClick={() => handleLanguageChange(language.code)}
            disabled={isPending}
            className={
              isDark
                ? `text-[#f5f5f5] focus:bg-white/10 focus:text-white ${
                    locale === language.code ? 'bg-white/10' : ''
                  }`
                : locale === language.code
                  ? 'bg-accent'
                  : ''
            }
          >
            <span className="mr-2">{language.flag}</span>
            {language.name}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
